import { CustomTooltip } from '@AMIEWEB/Common/Tooltips/CustomTooltip';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import { useDecision } from '@optimizely/react-sdk';
import { ClickAwayListener, List, ListItemButton, ListItemText, Paper } from '@mui/material';
import { AndOrOperator } from 'app/enums/Common';
import { AMNDivisionType } from 'app/models/enums/AMNDivisionType';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import RightArrow from 'app/assets/images/IconImages/RightArrow.svg';
import clsx from 'clsx';
import { ff_order_ui_allow_duplicate_skillsets } from 'app/constants/FeatureFlags/Order/Keys';

const useStyles = makeStyles()(theme => ({
  menuOption: {
    paddingLeft: '30px',
  },
  andOr: {
    fontSize: '13px',
    fontWeight: '600',

    height: '25px',
    alignItems: 'center',
    paddingLeft: '14px',
    display: 'flex',
  },
  popover: {
    padding: '20px 0px',
    width: '190px',
    backgroundColor: `${theme.palette.system.white}`,
    zIndex: '999',
    position: 'absolute',
    borderRadius: '4px',
    boxShadow: `${theme.palette.system.lightGrey} 0 2px 3px 1px`,
    marginTop: '-257px',
  },
  popoverOperation: {
    '& .MuiPaper-root': {
      padding: '5px 0px',
      width: '120px',
      marginTop: '-80px',
      marginLeft: '25px',
    },
  },
  dividerSwitch: {
    height: '12px',
    border: `1px solid ${theme.palette.system.main}`,
  },
  dividerPopover: {
    height: '12px',
    border: `1px solid ${theme.palette.system.lightGrey}`,
  },
  andOrButton: {
    height: '32px',
    borderRadius: '16px',
    '&:hover .expandIcon': {
      display: 'inline-block !important',
      marginTop: '20px',
    },
  },
  expandInitial: {
    display: 'none',
    transform: 'translateY(-50%) rotate(0deg)',
    transition: 'transform 0.3s ease',
  },
  expandFinal: {
    display: 'inline-block !important',
    transform: 'translateY(-50%) rotate(180deg)',
    marginTop: '20px',
  },
  disableOptions: {
    pointerEvents: 'none',
    opacity: '0.4',
  },
  buttonAdd: {
    width: '28px',
    height: '28px',
    minWidth: '28px',
    backgroundColor: `${theme.palette.system.main}`,
    '&:hover': {
      backgroundColor: `${theme.palette.system.lightSkyBlue}`,
    },
  },
  paper: {
    width: '250px',
    overflowX: 'hidden',
    overflowY: 'auto',
    boxShadow: `2px 5px 10px ${theme.palette.system.semiTransparentBlack}`,
  },
  listItem: {
    '& .MuiListItemText-root': {
      marginTop: 1,
      marginBottom: 1,
    },
    '& .MuiListItem-root': {
      padding: '0 20px',
      paddingLeft: 8,
    },
    '& .MuiListItemButton-root': {
      padding: '0 16px',
      paddingLeft: 8,
    },
    '& .MuiButtonBase-root': {
      padding: '0 20px',
    },
  },
  listItemText: {
    whiteSpace: 'normal',
    wordBreak: "break-word",
  },
  duplicateSkillSetpopover: {
    zIndex: 9999,
    marginLeft: '1px',
    marginTop: '-44px',
    maxHeight: '250px',
    maxWidth: '500px',
  },
}));

export const AndOrSwitchButton = ({ value, onChange }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const isAnd = `${value}` === AndOrOperator.And;
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOptionClick = operationValue => {
    handleClose();
    onChange(operationValue);
  };

  return (
    <>
      <div className={classes.dividerSwitch} />
      <Button
        aria-describedby={anchorEl ? 'add-experience-menu-popover' : undefined}
        variant="contained"
        onClick={handleClick}
        className={classes.andOrButton}
      >
        {isAnd
          ? t('facility.units.unitDetails.unitRequirements.and')
          : t('facility.units.unitDetails.unitRequirements.or')}
        <div className={`expandIcon ${classes.expandInitial} ${anchorEl ? classes.expandFinal : ''}`}>
          <ExpandMoreIcon />
        </div>
      </Button>
      <div className={classes.dividerSwitch} />
      <Popover
        id={anchorEl ? 'add-experience-menu-popover' : undefined}
        open={anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        className={classes.popoverOperation}
      >
        <div>
          <MenuItem
            onClick={() => handleOptionClick(isAnd ? AndOrOperator.Or : AndOrOperator.And)}
            className={classes.menuOption}
          >
            {isAnd
              ? t('facility.units.unitDetails.unitRequirements.or')
              : t('facility.units.unitDetails.unitRequirements.and')}
          </MenuItem>
        </div>
      </Popover>
    </>
  );
};

export const AndorButton = ({ handleAdd, handleDuplicate, value, groupIndex, skillSetIndex, facilityDivision }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { setValue, watch, register } = useFormContext();
  const [enableDuplicateSkillSet] = useDecision(ff_order_ui_allow_duplicate_skillsets);
  const [orOptionMenuAncorEl, setOrOptionMenuAncorEl] = React.useState(null);
  const isAnd = `${value}` === AndOrOperator.And;

  const disciplineValue = watch(`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.discipline`);
  const specialityValue = watch(`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.specialty`);
  const skillsets = watch(`skillSetGroup.groups[${groupIndex}].skillsets`);
  const [anchorEl, setAnchorEl] = useState(null);

  // #region Add/Duplicate Skillset Starts
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOptionClickDuplicate = (operationValue, index = -1) => {
    handleClose();
    handleDuplicate({ skillSetId: index });
    if (!value) {
      register(`skillSetGroup.groups[${groupIndex}].operation`);
    }
    setValue(`skillSetGroup.groups[${groupIndex}].operation`, operationValue, { shouldValidate: true });
  };
  const handleOptionClickAdd = operationValue => {
    handleClose();
    handleAdd();
    if (!value) {
      register(`skillSetGroup.groups[${groupIndex}].operation`);
    }
    setValue(`skillSetGroup.groups[${groupIndex}].operation`, operationValue, { shouldValidate: true });
  };

  // #region Duplicate OR  Skillset Starts
  const handleOrClose = () => {
    setOrOptionMenuAncorEl(null);
  };
  const listItemText = (skillsetGroup, index) => {
    return (
      <ListItemText
        primaryTypographyProps={{ className: classes.listItemText }}
        primary={`${index + 1}. ${skillsetGroup?.skillSet?.discipline?.object?.Abbreviation}-${
          skillsetGroup?.skillSet?.specialty?.label
        }${skillsetGroup?.skillSet?.subSpecialty ? '-' + skillsetGroup?.skillSet?.subSpecialty?.label : ''}`}
      />
    );
  };

  const orOptionsList = React.useMemo(
    () => (
    <List>
        {(skillsets || []).map((skillset, index) => (
        <ListItemButton
            key={index}
          className={classes.listItem}
          onClick={() => handleOptionClickDuplicate(AndOrOperator.Or, index)}
        >
          {listItemText(skillset, index)}
        </ListItemButton>
      ))}
    </List>
  ), [skillsets, listItemText]);
  // #endregion Duplicate OR  Skillset Ends
  // #endregion Add/Duplicate Skillset Ends

  return (
    <>
      <div className={classes.dividerPopover} />
      <ClickAwayListener onClickAway={handleClose}>
        <Paper onMouseEnter={handleClick}>
          <CustomTooltip
            tooltipContent={t('facility.units.unitDetails.unitRequirements.disableAndOr')}
            hideToolTip={disciplineValue && specialityValue}
            standardMargin
          >
            <Button
              aria-describedby={anchorEl ? 'add-experience-menu-popover' : undefined}
              variant="contained"
              onClick={handleClick}
              className={classes.buttonAdd}
              disabled={
                (facilityDivision === AMNDivisionType.amnNurse &&
                !enableDuplicateSkillSet.enabled) ||
                !disciplineValue ||
                !specialityValue
              }
            >
              <AddIcon />
            </Button>
          </CustomTooltip>

          {anchorEl &&
            (facilityDivision !== AMNDivisionType.amnNurse || enableDuplicateSkillSet.enabled) &&
            disciplineValue &&
            specialityValue && (
              <div className={classes.popover}>
                <div
                  className={`${
                    (value && !isAnd) ||
                    (facilityDivision === AMNDivisionType.amnNurse && enableDuplicateSkillSet.enabled)
                      ? classes.disableOptions
                      : ''
                  }`}
                >
                  <div className={classes.andOr}>{t('facility.units.unitDetails.unitRequirements.and')}</div>

                  <MenuItem onClick={() => handleOptionClickAdd(AndOrOperator.And)} className={classes.menuOption}>
                    {t('facility.units.unitDetails.unitRequirements.addNewSkillSet')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleOptionClickDuplicate(AndOrOperator.And)}
                    className={classes.menuOption}
                  >
                    {t('facility.units.unitDetails.unitRequirements.duplicateSkillSet')}
                  </MenuItem>
                </div>
                <div className={`${value && isAnd ? classes.disableOptions : ''}`}>
                  <div className={classes.andOr}>{t('facility.units.unitDetails.unitRequirements.or')}</div>
                  <MenuItem
                    onClick={() => handleOptionClickAdd(AndOrOperator.Or)}
                    className={clsx(classes.menuOption, {
                      [classes.disableOptions]:
                        facilityDivision === AMNDivisionType.amnNurse && enableDuplicateSkillSet.enabled,
                    })}
                  >
                    {t('facility.units.unitDetails.unitRequirements.addNewSkillSet')}
                  </MenuItem>
                  {
                    // #region Duplicate Skillset - OR Starts
                  }
                  {!enableDuplicateSkillSet.enabled ? (
                    <MenuItem
                      onClick={() => handleOptionClickDuplicate(AndOrOperator.Or)}
                      className={classes.menuOption}
                    >
                      {t('facility.units.unitDetails.unitRequirements.duplicateSkillSet')}
                    </MenuItem>
                  ) : (
                    <MenuItem
                      id="orDuplicateSkillSetItem"
                      onMouseEnter={e => {
                        setOrOptionMenuAncorEl(e.currentTarget);
                      }}
                      onMouseLeave={handleOrClose}
                      className={classes.menuOption}
                    >
                      {t('facility.units.unitDetails.unitRequirements.duplicateSkillSet')}
                      {(!value || !isAnd) && (
                        <img src={RightArrow} style={{ marginLeft: '10px' }} alt="Side Menu Arrow" />
                      )}
                      <Popover
                        open={Boolean(orOptionMenuAncorEl)}
                        anchorEl={orOptionMenuAncorEl}
                        hideBackdrop
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        className={classes.duplicateSkillSetpopover}
                        onClose={handleOrClose}
                      >
                        <ClickAwayListener onClickAway={handleOrClose}>
                          <Paper className={classes.paper}>{orOptionsList}</Paper>
                        </ClickAwayListener>
                      </Popover>
                    </MenuItem>
                  )}
                  {
                    // #endregion Duplicate Skillset-OR Ends
                  }
                </div>
              </div>
            )}
        </Paper>
      </ClickAwayListener>
    </>
  );
};
